/**
 * How big the instruments are drawn.
 *
 * The HUD is laid out in fixed pixels, for a screen about three times the width
 * of a phone: the ship console and the radio are 268 each, which is most of a
 * 375-point display *twice over*. On a desktop that is right and on a phone it
 * is a cockpit you cannot see past.
 *
 * ── A scale rather than a second layout ─────────────────────────────────────
 *
 * A narrow layout — panels that reflow, stack, or become tabs — is the real
 * answer and it is a great deal of work, panel by panel. This is the cheap one
 * that fixes the complaint: draw the same cockpit smaller. Everything stays
 * where it is relative to its corner, nothing reflows, and there is one number
 * to tune.
 *
 * ── What is deliberately not scaled ─────────────────────────────────────────
 *
 * The touch grid. Its cells are sized for a thumb, and a thumb does not get
 * smaller because the screen did — shrinking a control below about forty points
 * makes it something you miss rather than something you press.
 */

/**
 * The factor the instruments are drawn at on a device flown with fingers.
 *
 * Chosen so the two widest panels fit side by side: the console and the radio
 * are 268 each, which at this scale is 174 apiece and leaves room between them
 * on a 375-point screen. Small enough to see past, large enough to read on a
 * display that has three device pixels for every one of these.
 *
 * One number, and the one to change if it is still wrong.
 */
const TOUCH_HUD_SCALE = 0.65;

/**
 * Decided once, from the primary pointer rather than from the window size.
 *
 * A narrow *window* on a desktop is still a desktop — somebody has dragged it
 * that way and can drag it back, and shrinking their instruments for it would be
 * answering a question they did not ask. `pointer: coarse` is the honest test of
 * "this is being flown with fingers", which is when the fixed sizes stop fitting.
 */
export const TOUCH_UI: boolean = (() => {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    // No `matchMedia` is old enough that it is not a phone.
    return false;
  }
})();

export const HUD_SCALE: number = TOUCH_UI ? TOUCH_HUD_SCALE : 1;

/**
 * Whether to spell out which key does a thing.
 *
 * A prompt like "menu (esc)" is help on a keyboard and clutter without one:
 * there is no escape key to press, and the space it takes is a real fraction of
 * a phone. The control itself is unchanged — only the reminder goes.
 */
export const SHOW_KEY_HINTS = !TOUCH_UI;

/**
 * Handlers that make a button work under a *second* finger.
 *
 * ── Why `click` is not enough ───────────────────────────────────────────────
 *
 * A browser synthesises mouse events — and `click` is one — only for the
 * **primary** touch point. If a finger is already down, a second finger tapping
 * a button produces `pointerdown` and `pointerup` and no click at all. That is
 * the specified behaviour, not a bug to work around.
 *
 * Which is exactly the case this game asks for constantly: a thumb holding roll
 * while the other hand throws the thruster switch. The thumb grid always worked
 * — it is built on pointer events, and those fire for every touch point — and
 * every switch on the console did not, because they are ordinary `onClick`
 * buttons. One control held, one control dead, and no obvious difference
 * between them from the outside.
 *
 * ── One or the other, never both ────────────────────────────────────────────
 *
 * On a coarse pointer the tap is taken from `pointerup` and `onClick` is not
 * used, so a primary touch — which *does* get a click — cannot fire the button
 * twice. Anywhere else it stays a plain click, which keeps the keyboard's
 * activation of a focused button working, and that is worth more on a desktop
 * than multi-touch is.
 */
export function tapProps(onTap: () => void): {
  onClick?: () => void;
  onPointerUp?: (event: React.PointerEvent) => void;
} {
  if (!TOUCH_UI) return { onClick: onTap };

  return {
    onPointerUp: (event) => {
      // Primary button only: a pen's barrel button and a right-click both
      // report through here, and neither is a press of this control.
      if (event.button === 0) onTap();
    },
  };
}

/**
 * The style that draws a panel at the HUD scale, anchored to its own corner.
 *
 * `origin` has to match where the panel is pinned or it will drift away from its
 * edge as it shrinks: a panel at `top: 16, left: 16` scaled about its centre
 * ends up further into the screen than it was asked to be, by half of what it
 * lost.
 *
 * Returns nothing at all at full scale, so a desktop carries no transform it did
 * not have before — and in particular does not gain a stacking context, which is
 * the sort of thing that quietly reorders a HUD.
 */
export function hudScaled(origin: string): React.CSSProperties {
  if (HUD_SCALE === 1) return {};

  return { transform: `scale(${HUD_SCALE})`, transformOrigin: origin };
}
